import React, { FC, useRef, useState } from 'react'
import { Button, SpinnerIcon } from '@sistemiv/s-components'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowUpTrayIcon, PlusIcon } from '@heroicons/react/24/outline'
import NoProjectsIcon from '../assets/NoProjectsIcon'
import { useProjectContent } from '../repositories/project/projectContent.repository'
import AddFolderOrRenameModal from '../components/modals/AddFolderOrRenameModal'
import { useCreateDirectory } from '../repositories/directory/mutations/directory-create'
import ProjectContentsTable, { getFileIcon } from '../components/table/ProjectContentsTable'
import { useQueryClient } from 'react-query'
import { ChevronDownIcon } from '@heroicons/react/24/solid'
import { Menu } from '@headlessui/react'
import FilesAndDirectoriesService from '../services/FilesAndDirectoriesService'
import Breadcrumbs from '../components/layout/Breadcrumbs'

const ProjectPage: FC = () => {
  const { org, project } = useParams()
  const { t } = useTranslation()
  const { data: files, isLoading: isFilesLoading } = useProjectContent()
  const [openFolderModal, setOpenFolderModal] = useState(false)
  const { mutate: addFolder, isLoading: addingFolder } = useCreateDirectory()
  const queryClient = useQueryClient()

  const headers = [
    {
      accessor: 'name',
      label: t('name'),
    },
    {
      accessor: 'lastModified',
      label: t('modified'),
    },
    {
      accessor: 'totalSize',
      label: t('size'),
    },
    {
      accessor: 'actions',
      label: t('actions'),
    },
  ]

  const handleCreateFolder = (name: string) => {
    if (!org || !project) return
    addFolder(
      {
        organization: org,
        project: project,
        name,
      },
      {
        onSuccess: () => {
          setOpenFolderModal(false)
          queryClient.invalidateQueries({ queryKey: ['project-content', org, project] })
        },
      },
    )
  }
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
      fileInputRef.current.click()
    }
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])
    const filteredFiles = files.filter((file) =>
      ['form', 'bpmn', 'dmn', 'js'].some((ext) => file.name.split('.').pop()?.includes(ext)),
    )

    if (filteredFiles.length === 0) {
      alert('You have selected unsupported file types!')
      return
    }

    await Promise.all(
      filteredFiles.map((file) => FilesAndDirectoriesService.uploadFile(org!, `${project}/${file.name}`, file)),
    )

    queryClient.invalidateQueries({ queryKey: ['project-content', org, project] })
  }

  return (
    <div className='h-full w-full flex overflow-auto'>
      <div className='h-full w-full'>
        <div className='w-full h-[60px] flex justify-between border-b border-gray-300 items-center px-8'>
          <Breadcrumbs />
          <div className='flex gap-x-4'>
            <Menu as='div' className='relative'>
              <Menu.Button className='bg-sky-500 text-white !rounded-md flex items-center gap-x-2 py-2 px-2'>
                {' '}
                <PlusIcon className='w-4 h-4 text-white' />
                {t('create')}
                <ChevronDownIcon className='w-4 h-4 text-white' />
              </Menu.Button>
              <Menu.Items className='absolute right-0 z-10 mt-2 w-56 origin-top-right flex flex-col bg-white py-3 drop-shadow  rounded shadow-xl ring-1 ring-black ring-opacity-5'>
                <Menu.Item>
                  <Link
                    className='py-2 px-4 cursor-pointer w-full hover:bg-sky-50 active:bg-sky-100 flex items-center'
                    to={`modeler/bpmn/new`}
                  >
                    {getFileIcon('.bpmn')}
                    BPMN diagram
                  </Link>
                </Menu.Item>
                <Menu.Item>
                  <Link
                    className='py-2 px-4 cursor-pointer w-full hover:bg-sky-50 active:bg-sky-100 flex items-center'
                    to={`modeler/dmn/new`}
                  >
                    {getFileIcon('.dmn')}
                    DMN diagram
                  </Link>
                </Menu.Item>
                <Menu.Item>
                  <Link
                    className='py-2 px-4 cursor-pointer w-full hover:bg-sky-50 active:bg-sky-100 flex items-center'
                    to={`modeler/form/new`}
                  >
                    {getFileIcon('.form')}
                    Form
                  </Link>
                </Menu.Item>
              </Menu.Items>
            </Menu>
            {/* <Button className='bg-sky-500 text-white !rounded-md'>
              <>
                <PlusIcon className='w-4 h-4 text-white' />
                {t('create')}
                <ChevronDownIcon className='w-4 h-4 text-white' />
              </>
            </Button> */}
            <Button
              className='border border-blue-500 text-blue-500 !rounded-md hover:bg-blue-50'
              onClick={() => setOpenFolderModal(true)}
            >
              <>
                <PlusIcon className='w-4 h-4' />
                {t('folder')}
              </>
            </Button>
            <Button
              className='border border-blue-500 text-blue-500 !rounded-md hover:bg-blue-50'
              onClick={handleButtonClick}
            >
              <ArrowUpTrayIcon className='w-4 h-4' /> {t('upload')}
              <input
                ref={fileInputRef}
                type='file'
                className='hidden'
                onChange={handleFileChange}
                accept='.bpmn,.dmn,.form,.js'
                multiple
              />
            </Button>
          </div>
        </div>
        {isFilesLoading && (
          <div className='w-full flex justify-center pt-5'>
            <SpinnerIcon className='text-sky-500 h-7 w-7'></SpinnerIcon>
          </div>
        )}
        {!isFilesLoading &&
          (files?.contents.length === 0 ? (
            <div className='flex flex-col h-1/2 w-full mx-auto mt-14 items-center justify-start'>
              <NoProjectsIcon className='text-white fill-slate-400 w-20 h-20' />
              <p className='text-slate-400 font-medium'>{t('noFiles')}</p>
            </div>
          ) : (
            <div className='py-6'>
              <ProjectContentsTable headers={headers} rows={files?.contents || []} />
            </div>
          ))}
      </div>
      {openFolderModal && (
        <AddFolderOrRenameModal
          open={openFolderModal}
          setOpen={setOpenFolderModal}
          onCancel={() => setOpenFolderModal(false)}
          onCreate={handleCreateFolder}
          creating={addingFolder}
          mode='create'
        />
      )}
    </div>
  )
}

export default ProjectPage
